"use client";

import { ChevronUpIcon } from "@/assets/icons";
import { cn } from "@/lib/utils";
import { useId, useState } from "react";

type PropsType = {
  label: string;
  items: { value: string; label: string; disabled?: boolean }[];
  prefixIcon?: React.ReactNode;
  className?: string;
  error?: string;
  name: string;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  required?: boolean;
  value?: string;
} & (
  | { placeholder?: string; defaultValue: string }
  | { placeholder: string; defaultValue?: string }
);

export function Select({
  items,
  label,
  defaultValue,
  placeholder,
  prefixIcon,
  className,
  error,
  name,
  onChange,
  required,
  value,
}: PropsType) {
  const id = useId();
  const [internalValue, setInternalValue] = useState(defaultValue || "");
  const isControlled = value !== undefined;

  // Add validation for duplicate values
  const uniqueItems = items.reduce(
    (acc, item) => {
      if (!acc.some((x) => x.value === item.value)) {
        acc.push(item);
      }
      return acc;
    },
    [] as typeof items
  );

  if (uniqueItems.length !== items.length) {
    console.warn("Select component: Duplicate values found in items array");
  }

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!isControlled) {
      setInternalValue(e.target.value);
    }
    if (onChange) onChange(e);
  };

  const currentValue = isControlled ? value : internalValue;

  return (
    <div className={cn("space-y-3", className)}>
      <label
        htmlFor={id}
        className="block text-body-sm font-medium text-dark dark:text-white"
      >
        {label}
        {required && <span className="ml-1 select-none text-red">*</span>}
      </label>

      <div className="relative">
        {prefixIcon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2">
            {prefixIcon}
          </div>
        )}

        <select
          id={id}
          name={name}
          value={currentValue}
          onChange={handleChange}
          className={cn(
            "w-full appearance-none rounded-lg border border-stroke bg-transparent px-5.5 py-3 outline-none transition focus:border-primary active:border-primary dark:border-dark-3 dark:bg-dark-2 dark:focus:border-primary [&>option]:text-dark-5 dark:[&>option]:text-dark-6",
            currentValue && "text-dark dark:text-white",
            prefixIcon && "pl-11.5",
            error && "border-red-500"
          )}
        >
          {placeholder && (
            <option value="" disabled hidden>
              {placeholder}
            </option>
          )}

          {uniqueItems.map((item) => (
            <option
              key={`${item.value}-${item.label}`} // More unique key
              value={item.value}
              disabled={item.disabled}
            >
              {item.label}
            </option>
          ))}
        </select>

        <ChevronUpIcon className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 rotate-180" />
      </div>

      {error && <p className="text-body-sm text-red-500 mt-1">{error}</p>}
    </div>
  );
}
